import { StackFrame, Event } from '@sentry/react';

interface SentryCapturedMessageDetails {
    values: Array<{
        type: string;
        value: string | undefined;
        stacktrace: { frames: StackFrame[] };
    }>;
}

// Converts a Sentry captureMessage event (with error details stored in context)
// into a structured exception format that resembles what captureException would produce.
// This allows the error to be properly displayed in Sentry's issue details with stack traces.
export const getSentryCapturedMessageDetails = (event: Event): SentryCapturedMessageDetails => {
    const errorDetails = event.contexts?.error_details as
        | { name?: string; message?: string; stack?: string }
        | undefined;
    const { message } = event;
    const stackLines = errorDetails?.stack?.split('\n') || [];

    // Stack frame regexes for different browser formats:
    // V8 (Chrome, Edge, Opera): "at functionName (file.js:123:45)" or "at file.js:123:45"
    const v8FrameWithFunctionRegex = /^\s*at\s+(.+?)\s+\((.+?):(\d+):(\d+)\)/;
    const v8FrameWithoutFunctionRegex = /^\s*at\s+(.+?):(\d+):(\d+)/;
    // Firefox & Safari: "functionName@file.js:123:45"
    const firefoxSafariRegex = /^\s*(.+?)@(.+?):(\d+):(\d+)/;

    const frames: StackFrame[] = stackLines
        .slice(1) // Skip the error message line
        .map((line: string): StackFrame | null => {
            // Try V8 format first (most common)
            const v8Match1 = v8FrameWithFunctionRegex.exec(line);
            if (v8Match1) {
                const [, functionName, filename, lineNo, colNo] = v8Match1;
                return {
                    function: functionName,
                    filename,
                    lineno: parseInt(lineNo, 10),
                    colno: parseInt(colNo, 10),
                };
            }

            const v8Match2 = v8FrameWithoutFunctionRegex.exec(line);
            if (v8Match2) {
                const [, filename, lineNo, colNo] = v8Match2;
                return {
                    function: undefined,
                    filename,
                    lineno: parseInt(lineNo, 10),
                    colno: parseInt(colNo, 10),
                };
            }

            // Fallback to Firefox/Safari format
            const firefoxMatch = firefoxSafariRegex.exec(line);
            if (firefoxMatch) {
                const [, functionName, filename, lineNo, colNo] = firefoxMatch;
                return {
                    function: functionName,
                    filename,
                    lineno: parseInt(lineNo, 10),
                    colno: parseInt(colNo, 10),
                };
            }

            return null;
        })
        .filter((frame): frame is NonNullable<typeof frame> => frame !== null)
        .reverse(); // Reverse to match typical stack trace order (bottom to top)

    return {
        values: [
            {
                type: errorDetails?.name || 'Error',
                value: errorDetails?.message || message,
                stacktrace: { frames },
            },
        ],
    };
};
